import { defineChatTool } from "@codenexus/react";
import type { ChatTool } from "@codenexus/react";

/**
 * Build the `switch_open_document` chat tool. When invoked, the tool emits a
 * `navigate_to_section` UI event with the requested docId. The host
 * (AIChatPanel) listens for the event, checks whether the currently-mounted
 * editor has unresolved staged edits, optionally prompts the user with a
 * discard-confirmation modal, and dispatches the Redux action to swap the
 * active section.
 *
 * The tool returns synchronously with `status: 'requested'` — the agent does
 * NOT see the user's accept/decline of the discard prompt directly. After the
 * host completes the swap, the section change flows back to the agent via
 * the bridge's normal pushContext path (`open_documents` updates + a
 * `host_navigation` note from DocumentContextWatcher), so the agent's next
 * turn already has the new active section in [ui-context].
 *
 * Why a separate tool rather than baking section switching into
 * `edit_open_document`: switching is a host-side UX concern (it remounts the
 * editor, may discard unresolved staging, scrolls the user's view) and
 * deserves explicit consent — both from the user (via the modal) and from
 * the agent (which has to explicitly request it).
 */
export function createSwitchDocumentTool(): ChatTool {
    const tool = defineChatTool<{ docId: string }, { navigate_to_section: { docId: string } }>({
        name: "switch_open_document",
        category: "mutation",
        description:
            "Ask the host to switch the editor to a different open document section. " +
            "Use this when the user asks to work on a section that isn't currently active " +
            "(e.g., 'switch to Target Users', 'open the Solution Statement section', or when you " +
            "need to edit a section listed in open_documents that has `active: false`). " +
            "The host MAY prompt the user to confirm before discarding any pending staged edits " +
            "on the current section. Returns immediately with status 'requested'; the actual " +
            "switch surfaces back through open_documents context on your next turn.",
        parameters: {
            type: "object",
            properties: {
                docId: {
                    type: "string",
                    description:
                        "The docId of the section to switch to. Must match an id from open_documents."
                }
            },
            required: ["docId"],
            additionalProperties: false
        },
        execute: async (args, ctx) => {
            if (!args.docId) {
                return { status: "failed", reason: "missing docId" };
            }
            ctx.emit("navigate_to_section", { docId: args.docId });
            return {
                status: "requested",
                docId: args.docId,
                message:
                    "Requested host to switch sections. If the current section has pending staged edits, " +
                    "the user will be asked to confirm discarding them before the switch happens."
            };
        }
    });
    return tool as unknown as ChatTool;
}
